import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import SegmentedControl, { type SegmentButton } from '../SegmentedControl';
import { VIEW_TOGGLE_LABELS } from '../../constants/messages.constant';
import styles from './styles';

export type ViewToggleView = 'schedule' | 'standings';

export interface ViewToggleProps {
  activeView: ViewToggleView;
  seasonKey: string;
}

// Each value is the route segment of its view.
const VIEW_BUTTONS: Array<SegmentButton> = [
  { name: VIEW_TOGGLE_LABELS.SCHEDULE, value: 'schedule' },
  { name: VIEW_TOGGLE_LABELS.STANDINGS, value: 'standings' },
];

/**
 * Switches a season between its schedule and standings views. Selecting the
 * other view navigates to its bare route for the same season key, so the
 * current view's query params are dropped. Meant as a child of a wrapping
 * filter row: it fills a row of its own above the filters on phones and ends
 * the row from `sm` up.
 */
const ViewToggle = ({ activeView, seasonKey }: ViewToggleProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const handleViewChange = useCallback(
    (view: string | number) => navigate(`/${view}/${seasonKey}`),
    [navigate, seasonKey]
  );

  return (
    <SegmentedControl
      buttons={VIEW_BUTTONS}
      selectedButton={activeView}
      onClickButton={handleViewChange}
      width={isPhone ? 'full' : 'inline'}
      testId="view-toggle"
      sx={styles.toggle}
    />
  );
};

export default ViewToggle;
